import _ from "lodash";
import moment from "moment";
import { MONTHS_OF_YEAR, MONTHS_OF_YEAR_MAP, DAYS_OF_WEEK_MAP } from "../constants";

/**
 * @param {Array} as
 * @param {Array} bs
 *
 * @returns {boolean}
 */
export const eqSet = (as, bs) => [...as].every((a) => bs.has(a)) && [...bs].every((a) => as.has(a));

const LOWERCASE_MONTHS_OF_YEAR = MONTHS_OF_YEAR.map((month) => month.toLowerCase());

export const ascending = (a, b) => a - b;
export const monthSort = (monthA, monthB) =>
  LOWERCASE_MONTHS_OF_YEAR.indexOf(monthA) - LOWERCASE_MONTHS_OF_YEAR.indexOf(monthB);

export const mergeUniq = (arr1, arr2) => [...new Set([...arr1, ...arr2])];
export const sameTimes = (r1, r2) => r1.startTime === r2.startTime && r1.endTime === r2.endTime;
export const sameMonths = (r1, r2) => eqSet(new Set(r1.months), new Set(r2.months));
export const sameWeekDays = (r1, r2) => eqSet(new Set(r1.week_days), new Set(r2.week_days));
export const sameMonthDays = (r1, r2) => eqSet(new Set(r1.month_days), new Set(r2.month_days));

export const capitalize = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export const makeTime = (luxonInstance) => {
  const { hour, minute } = luxonInstance.toObject();
  return `${makeDblDigit(hour)}:${makeDblDigit(minute)}`;
};

export const circularArrayStep = (array, index = 0, step = 1) => {
  if (array.length <= index + step) return array[index + step - array.length];
  if (index + step < 0) return array[array.length + step];
  return array[index + step];
};

export const makeDblDigit = (input) => {
  const n = Number(input);
  if (n < 10 && n >= 0) return `0${n}`;
  if (n >= 10 && n < 100) return String(n);
  return null;
};

export const monthNameOrNum = (input) => {
  let monthNum;
  let monthName;
  if (Number(input)) {
    monthNum = Number(input);
    if (monthNum >= 1 && monthNum <= 12) {
      monthName = MONTHS_OF_YEAR[monthNum - 1];
      return monthName.toLowerCase();
    }
    return null;
  }
  monthName = capitalize(input.toLowerCase());
  if (MONTHS_OF_YEAR.includes(monthName)) {
    monthNum = MONTHS_OF_YEAR.indexOf(monthName) + 1;
    return makeDblDigit(monthNum);
  }
  return null;
};

export class CyclicArray extends Array {
  /**
   * if non-empty, takes the first element and moves it to the end
   * @returns {Array}
   */
  rotate() {
    if (this.length > 0) this.push(this.shift());

    return this;
  }
}

function getDaysInFebruary(year = new Date().getFullYear()) {
  return year % 4 === 0 ? 29 : 28;
}

/**
 * Thirty days hath September,
 * April, June, and November
 * all the rest have thirty-one
 * except for February, which has {getDaysInFebruary()}
 *
 * @param {string} month spelled out in lowercase
 * @returns {number}
 */
export function getDaysInMonth(month) {
  switch (month) {
    case "september":
    case "april":
    case "june":
    case "november":
      return 30;
    case "february":
      return getDaysInFebruary();
    default:
      return 31;
  }
}

/**
 * Takes an array of days/months and sorts them in proper calendar order.
 * @param {*} inputArray
 * @returns {Array<string>} an array of days/months sorted in proper calendar order.
 */

export const sortWeekDaysAndMonths = (inputArray) => {
  const output = [];
  const sortedInput = [];
  let referenceMap = {};

  if (typeof inputArray[0] === "number") {
    return inputArray;
  }

  if (typeof inputArray[0] === "string" && !_.isNil(inputArray) && inputArray[0].includes("day")) {
    referenceMap = DAYS_OF_WEEK_MAP;
  } else {
    referenceMap = MONTHS_OF_YEAR_MAP;
  }

  for (let i = 0; i < inputArray.length; i++) {
    const currentSelection = inputArray[i];
    sortedInput.push(referenceMap[currentSelection]);
  }

  sortedInput.sort(ascending);
  sortedInput.forEach((value) => {
    const result = Object.keys(referenceMap).find((key) => referenceMap[key] === value);
    output.push(result);
  });
  return output;
};

/**
 *
 * @param {String} baseURL
 * @param {Object} activeReservation
 * @returns {String} returns the Kibana URL String with relevant query params passed in
 */

export const getKibanaURL = (baseURL, activeReservation) => {
  if (!activeReservation?.trip) return null;
  const { vehicleId } = activeReservation;
  const { start, stop } = activeReservation?.trip;

  const from = moment(start).subtract(10, "m").toISOString();
  const to = moment(stop).add(10, "m").toISOString();

  const path = "/app/kibana#/dashboard/";
  const kibanaId = "8a5bcfc0-67d1-11ea-aad2-df25f3432f8a";
  const query = `?_a=(query:(language:kuery,query:'vehicle_id:%22${vehicleId}%22'))&_g=(time:(from:'${from}',to:'${to}'))`;

  return baseURL + path + kibanaId + query;
};

/**
 *
 * @param {Number[]} point An array of points [Long, Lat]
 * @param {Object} polygon  The polygon to which all points are compared.
 * @returns {Boolean}
 */

export const isPointInBoundry = (point, polyPoints) => {
  const [x, y] = point;

  let inside = false;
  // eslint-disable-next-line no-plusplus
  for (let i = 0, j = polyPoints.length - 1; i < polyPoints.length; j = i++) {
    const xi = polyPoints[i][0];
    const yi = polyPoints[i][1];
    const xj = polyPoints[j][0];
    const yj = polyPoints[j][1];

    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }

  return inside;
};

/**
 *
 * @param {Object} hexes hex object containing an arrays of hexes.
 * @param {Array} mainPolygon an array of polygons containing points [long, lat]
 * @returns {Boolean}
 * @Note : This filtering heuristic is the greediest of the bunch.
 *         It filters out all hexes that touches the vertices of the polygon.
 */

export const greedyFilteringHeuristic = (hexes, mainPolygon) => {
  let allPointsIn = true;
  // eslint-disable-next-line no-restricted-syntax
  for (const points of hexes) {
    const inBoundry = isPointInBoundry(points, mainPolygon);
    if (inBoundry === false) {
      allPointsIn = !allPointsIn;
      break;
    }
  }
  return allPointsIn;
};

/**
 *
 * @param {Object} hexes hex object containing an arrays of hexes.
 * @param {Array} mainPolygon an array of polygons containing points [long, lat]
 * @returns {Boolean}
 * @Note : This filtering algorithm filters out all hexes whose centriod is close to the boundaries of the polygon.
 */

export const filterHeuristicUsingCentroidPoint = (hexes, mainPolygon) => {
  let x = 0;
  let y = 0;

  // Calculate the centroid.
  hexes.forEach((hex) => {
    x += hex[0];
    y += hex[1];
  });

  const xCentroid = x / hexes.length;
  const yCentroid = y / hexes.length;

  return isPointInBoundry([xCentroid, yCentroid], mainPolygon);
};

/**
 *
 * @param {Object} hexes hex object containing an arrays of hexes.
 * @param {Array} mainPolygon an array of polygons containing points [long, lat]
 * @returns {Boolean}
 * @Note : This filtering algorithm filters out all hexes with the smallest equidistance to the bounds of the polygon.
 */

export const equiDistanceFilterHeuristic = (hexes, mainPolygon) => {
  let x = 0;
  let y = 0;
  // Calculate centroid.
  hexes.forEach((hex) => {
    x += hex[0];
    y += hex[1];
  });
  const xCentroid = x / hexes.length;
  const yCentroid = y / hexes.length;

  let smallestDistance = Infinity;
  for (let i = 0; i < mainPolygon.length - 1; i++) {
    const p1 = mainPolygon[i];
    const p2 = mainPolygon[i + 1];
    const centerPoint = [(p1[0] + p2[0]) / 2, (p1[1] + p2[1]) / 2];
    const distvalue = Math.sqrt(
      (xCentroid - centerPoint[0]) ** 2 + (yCentroid - centerPoint[1]) ** 2,
    );
    smallestDistance = Math.min(smallestDistance, distvalue);
  }
  return smallestDistance <= 1;
};

export const filterHexesOutsidePolygon = (hexes, mainPolygon) => {
  const newHexInPolygon = {};
  Object.entries(hexes).forEach(([key]) => {
    const hexObject = hexes[key];
    const polygons = hexObject.polygon;
    const shouldIncludeHex = filterHeuristicUsingCentroidPoint(polygons, mainPolygon);
    if (shouldIncludeHex) {
      newHexInPolygon[key] = { ...hexObject };
    }
  });
  return newHexInPolygon;
};

/**
 *
 * @param {String} prefix? Error Message Prefix (if applicable)
 * @param {String} statusCode Error status code
 * @param {String} errorMessage API response string
 * @returns concatenated error string.
 */
export const errorMessageBuilder = (prefix, statusCode, errorMessage) =>
  `${prefix} : ${statusCode} : ${errorMessage} `;
